import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Spinner, EmptyState, Pill } from "@/components/common";
import { Plus, Pencil, Trash2, ShieldCheck, Eye, Check, X } from "lucide-react";

const MODULES = ["anagrafica", "tesseramenti", "certificati", "corsi", "presenze", "pagamenti", "contabilita", "documenti", "comunicazioni", "utenti"];
const MOD_LABEL = { anagrafica: "Anagrafica", tesseramenti: "Tesseramenti", certificati: "Certificati", corsi: "Corsi", presenze: "Presenze", pagamenti: "Pagamenti", contabilita: "Contabilità", documenti: "Documenti", comunicazioni: "Comunicazioni", utenti: "Utenti" };

export default function Utenti() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [persone, setPersone] = useState([]);
  const [roles, setRoles] = useState({ roles: {}, defaults: {} });
  const [open, setOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "segreteria", pin: "", person_id: "" });

  const load = () => {
    api.get("/utenti").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/persone").then((r) => setPersone(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!/^[A-Za-z0-9]{8}$/.test(form.pin)) { toast.error("PIN 8 caratteri alfanumerici"); return; }
    try { await api.post("/utenti", { ...form, person_id: form.person_id || null }); toast.success("Utente creato"); setOpen(false); setForm({ name: "", email: "", role: "segreteria", pin: "", person_id: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare utente?")) return; await api.delete(`/utenti/${id}`); load(); };

  if (!items) return <Spinner />;
  const roleOptions = Object.entries(roles.roles || {}).filter(([k]) => k !== "super_admin");

  return (
    <div>
      <PageHeader title="Utenti e Ruoli" subtitle="Permessi granulari per ruolo"
        actions={<Button onClick={() => setOpen(true)} data-testid="add-utente-btn"><Plus size={16} /> Nuovo utente</Button>} />
      <Card className="overflow-hidden mb-5"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200 bg-slate-50/60 text-left">{["Utente", "Email", "Ruolo", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold text-slate-500 uppercase text-xs tracking-wider">{h}</th>)}</tr></thead>
        <tbody>{items.map((u) => (
          <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`utente-row-${u.id}`}>
            <td className="px-4 py-3 font-semibold text-slate-800">{u.name}</td>
            <td className="px-4 py-3 text-slate-500">{u.email}</td>
            <td className="px-4 py-3"><Pill className="bg-sky-100 text-sky-700">{u.role_label}</Pill></td>
            <td className="px-4 py-3">{u.active ? <span className="text-emerald-600 text-xs font-semibold">Attivo</span> : <span className="text-slate-400 text-xs">Disabilitato</span>}</td>
            <td className="px-4 py-3 text-right">
              <div className="flex justify-end gap-1">
                <button onClick={() => setPermOpen(u)} title="Vedi permessi" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Eye size={15} /></button>
                {u.id !== user.id && <button onClick={() => del(u.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>}
              </div>
            </td>
          </tr>
        ))}</tbody>
      </table></div></Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Nuovo utente">
        <div className="space-y-4">
          <Input label="Nome" data-testid="utente-nome" value={form.name} onChange={(e) => upd("name", e.target.value)} />
          <Input label="Email" type="email" data-testid="utente-email" value={form.email} onChange={(e) => upd("email", e.target.value)} />
          <Select label="Ruolo" data-testid="utente-ruolo" value={form.role} onChange={(e) => upd("role", e.target.value)}>{roleOptions.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select>
          {["atleta", "genitore", "istruttore"].includes(form.role) && (
            <Select label="Persona collegata" value={form.person_id} onChange={(e) => upd("person_id", e.target.value)}><option value="">—</option>{persone.map((p) => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}</Select>
          )}
          <Input label="PIN (8 caratteri alfanumerici)" data-testid="utente-pin" maxLength={8} value={form.pin} onChange={(e) => upd("pin", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} className="font-mono tracking-widest" />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-utente-btn">Crea utente</Button></div>
        </div>
      </Modal>

      <Modal open={!!permOpen} onClose={() => setPermOpen(null)} title={`Permessi — ${permOpen?.role_label}`}>
        <div className="space-y-1">
          {MODULES.map((m) => {
            const lvl = (roles.defaults?.[permOpen?.role] || {})[m] || "none";
            return (
              <div key={m} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-700">{MOD_LABEL[m]}</span>
                {lvl === "edit" ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check size={14} /> Modifica</span>
                  : lvl === "view" ? <span className="flex items-center gap-1 text-xs font-semibold text-sky-600"><Eye size={14} /> Sola lettura</span>
                  : <span className="flex items-center gap-1 text-xs font-semibold text-slate-300"><X size={14} /> Nessuno</span>}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
